/**
 * @engine/input — Mouse Device
 *
 * Mouse input device with buttons, position, and scroll.
 */

import type { Vec2Like, MouseButton, MouseControl } from '../types';
import { InputDevice } from './InputDevice';
import { ButtonControl } from '../controls/ButtonControl';
import { Vector2Control } from '../controls/Vector2Control';

/**
 * Mouse input device.
 */
export class Mouse extends InputDevice {
  // Buttons
  readonly leftButton: ButtonControl;
  readonly rightButton: ButtonControl;
  readonly middleButton: ButtonControl;
  readonly backButton: ButtonControl;
  readonly forwardButton: ButtonControl;

  // Position & movement
  readonly position: Vector2Control;
  readonly delta: Vector2Control;
  readonly scroll: Vector2Control;

  constructor(index: number = 0) {
    super('mouse', index, 'Mouse');

    // Create buttons
    this.leftButton = new ButtonControl('leftButton');
    this.rightButton = new ButtonControl('rightButton');
    this.middleButton = new ButtonControl('middleButton');
    this.backButton = new ButtonControl('backButton');
    this.forwardButton = new ButtonControl('forwardButton');

    // Create vectors
    this.position = new Vector2Control('position');
    this.delta = new Vector2Control('delta');
    this.scroll = new Vector2Control('scroll');

    // Register all
    this.registerControl(this.leftButton);
    this.registerControl(this.rightButton);
    this.registerControl(this.middleButton);
    this.registerControl(this.backButton);
    this.registerControl(this.forwardButton);
    this.registerControl(this.position);
    this.registerControl(this.delta);
    this.registerControl(this.scroll);
  }

  // ============================================================================
  // Convenience Accessors
  // ============================================================================

  /**
   * Get button by index (0 = left, 1 = middle, 2 = right, 3 = back, 4 = forward)
   */
  getButton(index: number): ButtonControl | undefined {
    switch (index) {
      case 0:
        return this.leftButton;
      case 1:
        return this.middleButton;
      case 2:
        return this.rightButton;
      case 3:
        return this.backButton;
      case 4:
        return this.forwardButton;
      default:
        return undefined;
    }
  }

  /**
   * Get button by name
   */
  getButtonByName(name: MouseButton): ButtonControl | undefined {
    return this.controls.get(name) as ButtonControl | undefined;
  }

  // ============================================================================
  // State Updates
  // ============================================================================

  /**
   * Update button state
   */
  setButtonState(button: number | MouseButton, pressed: boolean, time: number): void {
    const control =
      typeof button === 'number' ? this.getButton(button) : this.getButtonByName(button);
    control?.update(pressed, time);
  }

  /**
   * Update position
   */
  setPosition(x: number, y: number, time: number): void {
    this.position.update({ x, y }, time);
  }

  /**
   * Update movement delta
   */
  setDelta(dx: number, dy: number, time: number): void {
    this.delta.update({ x: dx, y: dy }, time);
  }

  /**
   * Update scroll delta
   */
  setScroll(dx: number, dy: number, time: number): void {
    this.scroll.update({ x: dx, y: dy }, time);
  }

  // ============================================================================
  // Frame Management
  // ============================================================================

  override beginFrame(): void {
    // Reset deltas at frame start (they accumulate during the frame)
    this.delta.update({ x: 0, y: 0 }, 0);
    this.scroll.update({ x: 0, y: 0 }, 0);
  }

  // ============================================================================
  // Control Resolution
  // ============================================================================

  override getControl(name: string): ButtonControl | Vector2Control | undefined {
    // Check registered controls first
    const control = super.getControl(name);
    if (control) return control as ButtonControl | Vector2Control;

    // Aliases
    switch (name) {
      case 'left':
        return this.leftButton;
      case 'right':
        return this.rightButton;
      case 'middle':
        return this.middleButton;
      default:
        return undefined;
    }
  }
}
